import { useCallback, useEffect, useRef, useState } from "react"
import { Send, Loader2, Sparkles, User } from "lucide-react"
import { toast } from "sonner"
import { ForgeButton } from "@/components/ui/forge-button"
import { Textarea } from "@/components/ui/textarea"
import { MessageMarkdown } from "@/components/domain/message-markdown"
import { cn } from "@/lib/cn"
import { consumeSSE } from "@/lib/sse"

type ProposalSeed = {
  id: string
  kind: string
  input: Record<string, unknown>
  number?: number
}

type Props = {
  skillId: number
  skillVersionId?: number | null
  runId?: number | null
  discussPayloadPath?: (runId: number) => string
  /** Optional seed used when there is no TestRun yet. */
  seedMessage?: string | null
  /**
   * Hoist proposal-stream events to the parent so the right pane can
   * render diffs + apply. When omitted, proposals are silently dropped
   * (true triage-only mode).
   */
  onProposal?: (proposal: ProposalSeed) => void
  /**
   * Notify the parent when the seeded payload (with snapshot data)
   * arrives. The parent uses it to populate the diff cards' before-state.
   */
  onSeedLoaded?: (payload: { skillSnapshot?: Record<string, unknown>; scenarios?: Array<Record<string, unknown>> }) => void
}

type Turn = {
  id: number
  role: "user" | "assistant"
  content: string
  createdAt: string
}

const SCROLL_BOTTOM_THRESHOLD = 96

// Inline chat with Forge embedded inside the Test Lab Forge tab (and the
// rehearsal-results modal). Streams via /skills/:id/ai_chat with persist=false
// so it doesn't pollute the persisted designer thread.
//
// Proposals: hoisted to the parent via onProposal when supplied. The
// The approval pane subscribes and renders ProposalDiffCard + "Apply selected"
// against the same /skills/:id/apply_proposals endpoint the DesignerPane uses.
export function ForgeQuickChat({ skillId, skillVersionId = null, runId, discussPayloadPath, seedMessage, onProposal, onSeedLoaded }: Props) {
  const [turns, setTurns]                 = useState<Turn[]>([])
  const [draft, setDraft]                 = useState("")
  const [streaming, setStreaming]         = useState(false)
  const [contextLoaded, setContextLoaded] = useState(false)
  const [contextSeed, setContextSeed]     = useState<string | null>(null)
  const [hideSeed, setHideSeed]           = useState(false)
  const threadRef = useRef<HTMLDivElement | null>(null)
  const stickToBottomRef = useRef(true)

  function onThreadScroll() {
    const el = threadRef.current
    if (!el) return
    stickToBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_BOTTOM_THRESHOLD
  }

  const lastTurn = turns[turns.length - 1]
  const tailTextLength = lastTurn?.content.length ?? 0

  // Keep tailing Forge while the assistant text streams into the same
  // bubble. If the operator scrolls upward, leave their scroll position
  // alone until they send a new message or return near the bottom.
  useEffect(() => {
    if (!stickToBottomRef.current) return
    requestAnimationFrame(() => {
      const el = threadRef.current
      if (!el || !stickToBottomRef.current) return
      el.scrollTop = el.scrollHeight
    })
  }, [turns.length, tailTextLength, streaming, hideSeed])

  const streamReply = useCallback(async (history: Turn[]) => {
    setStreaming(true)
    const body: Record<string, string> = {
      messages: JSON.stringify(history),
      persist:  "false",
    }
    // Run-scoped persistence: the controller side-effects each turn into
    // test_runs.forge_chat. Wiped automatically when the scenario reruns.
    if (runId != null) body.test_run_id = String(runId)
    if (skillVersionId != null) body.skill_version_id = String(skillVersionId)
    await consumeSSE(
      `/skills/${skillId}/ai_chat`,
      {
        method: "POST",
        body: new URLSearchParams(body),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      },
      {
        onText: (delta) => {
          setTurns((cur) => {
            const next = [...cur]
            const last = next[next.length - 1]
            if (last && last.role === "assistant") next[next.length - 1] = { ...last, content: last.content + delta }
            return next
          })
        },
        onToolProposal: (raw) => {
          const p = raw as { id?: string; kind?: string; input?: Record<string, unknown>; number?: number }
          if (!onProposal) return
          onProposal({
            id:     p.id ?? `prop-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            kind:   p.kind ?? "unknown",
            input:  p.input ?? {},
            number: p.number,
          })
        },
        onError: (msg) => {
          toast.error(`Forge error: ${msg}`)
        },
        onEnd: () => {
          setStreaming(false)
        },
      },
    )
    setStreaming(false)
  }, [skillId, skillVersionId, runId, onProposal])

  const sendText = useCallback(async (rawText: string) => {
    const text = rawText.trim()
    if (streaming || !contextLoaded || !text) return
    const now = new Date().toISOString()
    const baseId = Date.now()
    const seedTurn = turns.length === 0 && contextSeed?.trim()
      ? [{ id: baseId - 1, role: "user" as const, content: contextSeed.trim(), createdAt: now }]
      : []
    const newHistory: Turn[] = [
      ...turns,
      ...seedTurn,
      { id: baseId,     role: "user",      content: text, createdAt: now },
      { id: baseId + 1, role: "assistant", content: "",   createdAt: now },
    ]
    if (seedTurn.length > 0) setHideSeed(true)
    stickToBottomRef.current = true
    setTurns(newHistory)
    await streamReply(newHistory.slice(0, -1))
  }, [turns, streaming, contextLoaded, contextSeed, streamReply])

  // On mount: fetch the run context (discuss_payload) and hold it as a
  // hidden first-turn seed. Forge never sends on its own; the seed is only
  // included once the operator asks for help.
  // The run path also returns skillSnapshot + scenarios; we hoist those
  // to the parent so the diff pane can paint.
  useEffect(() => {
    let cancelled = false
    setTurns([])
    setDraft("")
    setContextLoaded(false)
    setContextSeed(null)
    setHideSeed(false)
    stickToBottomRef.current = true
    ;(async () => {
      try {
        if (runId == null) {
          if (!cancelled) {
            setContextSeed(seedMessage?.trim() || null)
            setContextLoaded(true)
          }
          return
        }

        const path = discussPayloadPath ? discussPayloadPath(runId) : `/skills/${skillId}/test_runs/${runId}/discuss_payload`
        const res = await fetch(path, {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({})) as { error?: string }
          if (!cancelled) {
            toast.error(data.error || `Couldn't load run context (HTTP ${res.status})`)
            setContextLoaded(true)
          }
          return
        }
        const data = await res.json() as {
          message?: string
          skillSnapshot?: Record<string, unknown>
          scenarios?: Array<Record<string, unknown>>
          forgeChat?: Array<{ role: string; content?: string | null; tool_proposal?: { id?: string; kind?: string; input?: Record<string, unknown>; number?: number } | null; created_at?: string }>
        }
        if (cancelled) return
        if (!data.message) {
          toast.error("No run context to send Forge.")
          setContextLoaded(true)
          return
        }
        setContextSeed(data.message)

        // Hydrate prior chat from this run (test_runs.forge_chat). The
        // server appended these as the operator chatted in earlier
        // sessions; reload restores the thread until the scenario reruns.
        // Prepend the run-context seed as a hidden first turn so Forge
        // still sees the failure background on every subsequent send.
        const persisted = data.forgeChat || []
        if (persisted.length > 0) {
          let idCounter = Date.now()
          const hydrated: Turn[] = [
            { id: idCounter++, role: "user", content: data.message, createdAt: new Date().toISOString() },
          ]
          for (const m of persisted) {
            if (m.role === "user" || m.role === "assistant") {
              hydrated.push({
                id:        idCounter++,
                role:      m.role,
                content:   m.content || "",
                createdAt: m.created_at || new Date().toISOString(),
              })
            } else if (m.role === "tool_proposal" && m.tool_proposal) {
              const p = m.tool_proposal
              onProposal?.({
                id:     p.id ?? `prop-${idCounter}`,
                kind:   p.kind ?? "unknown",
                input:  p.input ?? {},
                number: p.number,
              })
            }
          }
          setTurns(hydrated)
          setHideSeed(true)
        }

        setContextLoaded(true)
        onSeedLoaded?.({ skillSnapshot: data.skillSnapshot, scenarios: data.scenarios })
      } catch {
        if (!cancelled) {
          toast.error("Network error loading Forge context")
          setContextLoaded(true)
        }
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [skillId, runId, seedMessage, discussPayloadPath])

  // Hide the synthesized run/test context when it gets included as the
  // first turn. It is too noisy for the visible thread.
  const visibleTurns = turns.slice(hideSeed ? 1 : 0)
  const pendingAssistant = visibleTurns[visibleTurns.length - 1]
  const showThinkingRow =
    streaming &&
    pendingAssistant?.role === "assistant" &&
    pendingAssistant.content.trim().length === 0
  const renderedTurns = visibleTurns.filter((t) => {
    // We create an empty assistant turn before the first SSE delta so
    // deltas can append into a stable row. Don't render that placeholder;
    // otherwise the operator sees a blank Forge avatar plus the thinking row.
    return !(t.role === "assistant" && t.content.trim().length === 0)
  })

  return (
    <div className="valet-card flex h-full min-h-0 flex-col gap-4 rounded-card p-4">
      <div className="flex shrink-0 items-center gap-2 text-sm font-medium text-app-text">
        <span className="grid h-9 w-9 place-items-center rounded-full border border-vip-goldBorder bg-vip-goldMute text-vip-goldSoft shadow-goldGlow">
          <Sparkles size={16} className="forge-icon" />
        </span>
        <div>
          <div className="font-semibold text-white">Forge</div>
          <div className="text-[11px] text-app-muted">Ask for help or request changes</div>
        </div>
      </div>

      <div
        ref={threadRef}
        onScroll={onThreadScroll}
        className="thin-scrollbar min-h-0 flex-1 overflow-y-auto rounded-card border border-border bg-black/30 p-3"
      >
        {renderedTurns.length === 0 && !streaming && (
          <div className="flex h-full min-h-28 items-center justify-center px-4 text-center text-sm text-app-muted">
            {contextLoaded ? "Ask Forge for help with this test or run." : "Preparing Forge context…"}
          </div>
        )}
        {renderedTurns.length === 0 && streaming && (
          <div className="flex items-center justify-center gap-2 text-sm text-app-muted">
            <Loader2 size={14} className="animate-spin" />
            Forge is thinking…
          </div>
        )}
        <div className="flex flex-col gap-1.5">
          {renderedTurns.map((t) => (
            <ForgeChatTurnRow key={t.id} turn={t} />
          ))}
          {showThinkingRow && renderedTurns.length > 0 && <ForgeThinkingRow />}
        </div>
      </div>

      <div className="flex shrink-0 items-end gap-2">
        <Textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter inserts a newline.
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              if (!streaming && contextLoaded && draft.trim()) {
                const t = draft
                setDraft("")
                void sendText(t)
              }
            }
          }}
          placeholder={contextLoaded
            ? "Ask Forge for help — e.g. 'should we change the test or the skill?'"
            : "Preparing run context…"}
          rows={2}
          disabled={streaming}
          className="flex-1 rounded-control border-border bg-white/[0.04] focus:border-vip-goldBorder"
        />
        <ForgeButton
          size="sm"
          disabled={streaming || !contextLoaded || !draft.trim()}
          onClick={() => {
            if (!contextLoaded || !draft.trim()) return
            const t = draft
            setDraft("")
            void sendText(t)
          }}
        >
          {streaming ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        </ForgeButton>
      </div>

      <div className="shrink-0 text-[11px] text-app-muted">
        Enter to send · Shift+Enter for a new line
      </div>
    </div>
  )
}

function ForgeChatTurnRow({ turn }: { turn: Turn }) {
  const isUser = turn.role === "user"
  const time = formatTime(turn.createdAt)
  const label = isUser ? "You" : "Forge"

  return (
    <article className={cn("rounded-xl px-4 py-2.5", isUser ? "human-row" : "message-row")}>
      <div className="flex gap-3">
        <ForgeChatAvatar role={turn.role} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-white">{label}</span>
            <span className="text-xs text-app-subtle">{time}</span>
          </div>
          <div className="mt-0.5 text-sm leading-tight text-neutral-100">
            <MessageMarkdown content={turn.content} />
          </div>
        </div>
      </div>
    </article>
  )
}

// Subtitle escalates every 10s of waiting. Caps at 90s — the Anthropic
// SDK's per-request timeout (REQUEST_TIMEOUT_SECONDS in the
// agents/ai_model_providers/anthropic.rb provider). After that, our
// client raises and the indicator unmounts.
const FORGE_THINKING_TIERS: { atSeconds: number; subtitle: string }[] = [
  { atSeconds:  0, subtitle: "thinking" },
  { atSeconds: 10, subtitle: "still thinking…" },
  { atSeconds: 20, subtitle: "hard problem" },
  { atSeconds: 30, subtitle: "harder problem" },
  { atSeconds: 40, subtitle: "real puzzler" },
  { atSeconds: 50, subtitle: "this one's a beast" },
  { atSeconds: 60, subtitle: "heavy lifting" },
  { atSeconds: 70, subtitle: "cracking it open" },
  { atSeconds: 80, subtitle: "almost have it" },
  { atSeconds: 90, subtitle: "any second now…" },
]

function ForgeThinkingRow() {
  // Tick once per second. Resets each turn because the parent only
  // mounts this component while streaming + the assistant bubble is
  // still empty; on first text_delta the parent flips the gate and
  // unmounts ForgeThinkingRow, so a new turn always starts at 0.
  const [elapsed, setElapsed] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setElapsed((s) => s + 1), 1000)
    return () => clearInterval(id)
  }, [])
  const subtitle = FORGE_THINKING_TIERS.reduce(
    (acc, t) => elapsed >= t.atSeconds ? t.subtitle : acc,
    FORGE_THINKING_TIERS[0].subtitle,
  )

  return (
    <article className="message-row rounded-xl px-4 py-2.5">
      <div className="flex gap-3">
        <ForgeChatAvatar role="assistant" />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex items-baseline gap-2">
            <span className="font-semibold text-white">Forge</span>
            <span className="text-xs text-app-subtle">{subtitle}</span>
          </div>
          <div className="mt-2 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:0ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:200ms]" />
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-app-muted [animation-delay:400ms]" />
          </div>
        </div>
      </div>
    </article>
  )
}

function ForgeChatAvatar({ role }: { role: Turn["role"] }) {
  if (role === "user") {
    return (
      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-gradient-to-br from-red-500 to-red-700 text-white">
        <User size={16} strokeWidth={2} />
      </div>
    )
  }

  return (
    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-vip-gold/40 bg-vip-goldMute text-vip-goldSoft">
      <Sparkles size={15} strokeWidth={1.8} className="forge-icon" />
    </div>
  )
}

function formatTime(value: string) {
  return new Date(value).toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
  })
}
